<template>
  <div>
    <VCol
        class="px-2 d-flex pt-0"
        cols="12"
    >
      <span class="body-2">
        {{ $t('authForm.verificationInfo') }}
      </span>
    </VCol>
    <VCol
        class="px-2 pb-2 pt-0"
        cols="12"
    >
      <div class="verification-code d-flex w-100" :key="keyCode">
        <input
            v-for="index in 6"
            :key="index"
            :maxlength="1"
            name="code"
            type="number"
            placeholder="-"
            class="code-input body-1"
            :class="(isExpiredCode || errorCode) && 'expired-error'"
            :data-test="`verification-code-input-${index}`"
            required
            oninput="if (this.value.length > this.maxLength) this.value = this.value.slice(0, this.maxLength)"
            @input="onChange($event.target.value, index); changeInput($event)"
            @focus="$event.target.value = ''"
        >
      </div>
    </VCol>
    <VCol
        v-if="isWrongCode && !isExpiredCode"
        class="px-2 pb-3 pt-0"
        cols="12"
        col="12"
    >
      <span class="caption error--text">{{ $t('authForm.errors.verificationCodeIsWrong') }}</span>
    </VCol>
    <VCol
        v-if="isExpiredCode"
        class="px-2 pb-3 pt-0"
        cols="12"
        col="12"
    >
      <span class="caption error--text">{{ $t('authForm.errors.verificationCodeExpired') }}</span>
    </VCol>
    <VCol
        class="px-2 pb-5 pt-1"
        cols="12"
        col="12"
    >
      <VBtn
          class="w-100 border-radius-8"
          dark
          data-test="verify-code-button"
          :class="{'disabled': !isVerificationCodeFilled || isExpiredCode }"
          large
          color="brandingPrimary"
          @click="onSubmit"
      >
        <span class="button">
          {{ $t('general.next') }}
        </span>
      </VBtn>
    </VCol>
    <VCol
        class="px-2 pb-3 pt-0"
        cols="12"
        col="12"
    >
      <span class="body-1">{{ $t('authForm.haventGotCode') }}</span>
      <template v-if="!isLastGenerationOtp">
        <span
            v-if="!isResendAvailable"
            class="ml-2 body-1 textTertiary--text"
        >{{ $t('authForm.resendCodeTimer', { seconds: timeToResend }) }}</span>
        <a
            v-if="isResendAvailable"
            class="body-1 ml-2 text-decoration-underline textTeriary--text"
            data-test="resend-code-link"
            @click="resendCode"
        >{{ $t('authForm.resendCode') }}</a>
      </template>
      <router-link v-else :to="{ name: 'ContactUs' }"><a class='ml-2 body-1 text-decoration-none secondary--text'>{{ $t('authForm.contactUs')}}</a></router-link>
    </VCol>
  </div>
</template>

<script lang="ts">
export default {
  name: 'VerificationPhone',
};
</script>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import AuthErrorsEnum from '@slg/web-customer-shared/src/enum/AuthErrorsEnum';

const props = defineProps({
  receivedRemainingTime: {
    type: Number,
    default: null,
  },
  otpExpiredAt: {
    type: Number,
    default: 0,
  },
  phone: {
    type: String,
    default: '',
  },
  receivedErrorCode: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['resendCode', 'onSubmit']);

const verificationCode = ref([null, null, null, null, null, null]);
const attempts = ref(0);
const timeToExpiredOtp = ref<any>(0);
const timeToResend = ref(0);
const resendTimerIntervalId = ref<any>(null);
const otpExpiredTimerIntervalId = ref<any>(null);
const errorCode = ref('');
const timeNow = ref(0);
const isExpiredCode = ref(false);
const keyCode = ref(new Date().toString());

const isWrongCode = computed(() => (
  errorCode.value === AuthErrorsEnum.CUSTOMER_USER_REGISTRY_FAILED_OTP_IS_NOT_VALID_OR_EXPIRED
    || errorCode.value === AuthErrorsEnum.LOGIN_FAILED_INCORRECT_PHONE_OR_OTP
));
const isVerificationCodeFilled = computed(() => {
  const allValuesExist = verificationCode.value.every((item) => item !== null);
  return allValuesExist;
});
const isResendAvailable = computed(() => timeToResend.value <= 0);
const isLastGenerationOtp = computed(() => {
  const secondsInDay = 86400;
  return props.receivedRemainingTime === secondsInDay;
});

function resendAvailableCounter() {
  timeToResend.value -= 1;
  if (timeToResend.value === 0) {
    clearInterval(resendTimerIntervalId.value);
  }
}

function otpExpiredCounter() {
  timeToExpiredOtp.value -= 1;
  if (timeToExpiredOtp.value <= 0) {
    isExpiredCode.value = true;
    clearInterval(otpExpiredTimerIntervalId.value);
  }
}

watch(
  () => props.otpExpiredAt,
  async () => {
    if (props.receivedRemainingTime) {
      clearInterval(resendTimerIntervalId.value);
      clearInterval(otpExpiredTimerIntervalId.value);
      const secondsInDay = 86400;
      const secondsForLastAttempt = 300;
      isExpiredCode.value = false;
      timeToExpiredOtp.value = ((props.otpExpiredAt - new Date().valueOf()) / 1000).toFixed();
      timeToResend.value = (props.receivedRemainingTime !== secondsInDay)
        ? props.receivedRemainingTime
        : secondsForLastAttempt;
      resendTimerIntervalId.value = setInterval(resendAvailableCounter, 1000);
      otpExpiredTimerIntervalId.value = setInterval(otpExpiredCounter, 1000);
    }
  },
  { deep: true, immediate: true },
);

watch(
  () => props.receivedErrorCode,
  async () => {
    if (isVerificationCodeFilled.value) {
      errorCode.value = props.receivedErrorCode;
    }
  },
  { deep: true, immediate: true },
);

function clearOtpAndErrorCode(isNewOtpNeeded = true) {
  if (isNewOtpNeeded) {
    verificationCode.value = [null, null, null, null, null, null];
    attempts.value = 0;
  }
  errorCode.value = '';
  keyCode.value = new Date().toString();
}

function changeInput($event: any) {
  // eslint-disable-next-line no-unused-expressions
  $event.target?.nextElementSibling?.focus();
}

function resendCode() {
  clearOtpAndErrorCode();
  attempts.value = 0;
  emit('resendCode');
}

function onChange(v: any, index: number) {
  const next = verificationCode.value.slice();
  next[index - 1] = v;
  verificationCode.value = next;
}

function onSubmit() {
  attempts.value += 1;
  const maxAttempts = 6;
  const secondsInDay = 86400;
  if (attempts.value >= maxAttempts && props.receivedRemainingTime !== secondsInDay) {
    isExpiredCode.value = true;
  } else {
    emit('onSubmit', verificationCode.value.join(''), attempts.value);
  }
}
</script>
<style lang="scss" scoped>
.verification-code {
  height: 50px;
  .code-input {
    height: 100%;
    width: 15%;
    border-bottom: 1px solid;
    margin-right: 8px;
    color: var(--v-textPrimary-base);
    text-align: center;
    -webkit-box-sizing: border-box;
    box-sizing: border-box;
    border-radius: 0;
    -webkit-appearance: initial;

    &.expired-error {
      border-bottom-color: var(--v-error-base);
    }
  }
  .code-input:last-child {
    margin-right: 0;
  }
}
</style>
